import React, { useState, useEffect, useMemo, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { Controller, useWatch } from "react-hook-form";
import { cn } from "utils/shadcn";

import { Label } from "app/components/ui/label";
import { RadioGroup, RadioGroupItem } from "app/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "app/components/ui/select";
import { useGlobalSlice } from "app/slice";
import { useDispatch } from "react-redux";
import {
  getSimTypeAllowance,
  resolveSimTypeForCapabilities,
  effectiveRadioSimValue,
  buildLineCompatibility,
  isSameCompatPayload,
} from "../../utils/simTypeCapabilities";

interface Props {
  control: any;
  setValue: any;
  imeiNumber: string;
}

export const PhoneSelector = ({ control, setValue, imeiNumber }: Props) => {
  const dispatch = useDispatch();
  const {
    useLazyGetDevicesQuery,
    useCheckDeviceCompatibilityMutation,
    actions,
  } = useGlobalSlice();

  const [getDevices, { data: devicesData }] = useLazyGetDevicesQuery();
  const [checkCompatibility, { isLoading: checkLoading }] =
    useCheckDeviceCompatibilityMutation();
  const { setCompatResult } = useOutletContext<{
    compatResult: any;
    setCompatResult: any;
  }>();

  const [brandSelect, setBrandSelect] = useState(false);
  const [modelSelect, setModelSelect] = useState(false);
  /** Compatibility row only used when it matches the current brand+model (avoids stale mutation data). */
  const [matchedCompat, setMatchedCompat] = useState<{
    brand: string;
    model: string;
    row: any;
  } | null>(null);

  const skipNextSimClear = useRef(true);
  const lastSyncedCompatRef = useRef<Record<string, unknown> | null>(null);

  const brand = useWatch({ control, name: "brand" });
  const model = useWatch({ control, name: "model" });
  const simType = useWatch({ control, name: "simType" });

  const uniqueBrands = useMemo(() => {
    const seen = new Set();
    return (
      devicesData?.data?.filter((item) => {
        if (seen.has(item.brand)) return false;
        seen.add(item.brand);
        return true;
      }) || []
    );
  }, [devicesData]);

  const filteredModels = useMemo(() => {
    return devicesData?.data?.filter((item) => item.brand === brand) || [];
  }, [brand, devicesData]);

  const capabilities =
    brand &&
    model &&
    matchedCompat &&
    matchedCompat.brand === brand &&
    matchedCompat.model === model
      ? matchedCompat.row?.deviceSupport?.simCapabilities
      : null;

  const { eSimAllowed, physicalSimAllowed } = getSimTypeAllowance(capabilities);

  useEffect(() => {
    getDevices({ networkType: 1 });
  }, [getDevices]);

  // Clear SIM type when brand/model change (not on initial mount — preserves saved flow until user edits device).
  useEffect(() => {
    if (skipNextSimClear.current) {
      skipNextSimClear.current = false;
      return;
    }
    setValue("simType", "", { shouldDirty: true, shouldValidate: true });
    setMatchedCompat(null);
    lastSyncedCompatRef.current = null;
    setCompatResult(null);
    dispatch(actions.setCompatibilityData({ CD: null }));
  }, [brand, model, setValue, dispatch, actions, setCompatResult]);

  // Fetch compatibility for current device; only apply result if still the active selection.
  useEffect(() => {
    if (!brand || !model) {
      setMatchedCompat(null);
      return;
    }

    let cancelled = false;
    const b = brand;
    const m = model;

    checkCompatibility({ devices: [{ brand: b, model: m }] }).then(
      (response: any) => {
        if (cancelled) return;
        const row = response?.data?.data?.[0];
        if (!row) return;
        setMatchedCompat({ brand: b, model: m, row });
      }
    );

    return () => {
      cancelled = true;
    };
  }, [brand, model, checkCompatibility]);

  // When capabilities are known, drop invalid simType and apply single-option default.
  useEffect(() => {
    if (!brand || !model || !capabilities) return;

    const current = simType || "";
    const resolved = resolveSimTypeForCapabilities(
      current,
      eSimAllowed,
      physicalSimAllowed
    );

    if (resolved !== current) {
      setValue("simType", resolved, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [
    brand,
    model,
    capabilities,
    eSimAllowed,
    physicalSimAllowed,
    simType,
    setValue,
  ]);

  // Push compatibility + sim selection to outlet + Redux (aligned row only).
  useEffect(() => {
    if (
      !matchedCompat ||
      matchedCompat.brand !== brand ||
      matchedCompat.model !== model
    ) {
      return;
    }

    if (!simType) return;

    const compatibilityData = buildLineCompatibility(
      matchedCompat.row,
      simType,
      imeiNumber
    );

    if (isSameCompatPayload(lastSyncedCompatRef.current, compatibilityData)) {
      return;
    }

    lastSyncedCompatRef.current = compatibilityData;
    setCompatResult(compatibilityData);
    dispatch(actions.setCompatibilityData({ CD: compatibilityData }));
  }, [
    brand,
    model,
    matchedCompat,
    dispatch,
    actions,
    setCompatResult,
    simType,
    imeiNumber,
  ]);

  const checkDataForBanner =
    matchedCompat &&
    matchedCompat.brand === brand &&
    matchedCompat.model === model
      ? { data: [matchedCompat.row] }
      : null;

  return (
    <div className="bg-white w-full p-4 sm:p-5 lg:p-6 border border-[#E5E7EB] rounded-xl md:rounded-2xl">
      <div className="mb-3 sm:mb-4 lg:mb-6">
        <h4 className="text-xl font-bold leading-5 text-black sm:text-[1.35rem] lg:text-2xl sm:leading-6 lg:leading-7 font-mobile mb-0.5 sm:mb-1">
          Pick your phone type
        </h4>
        <p className="text-xs font-medium leading-5 text-black opacity-50 sm:text-[0.825rem] md:text-sm">
          This information has to match your current service provider.
        </p>
      </div>
      <div className="space-y-4 sm:space-y-[1.125rem] lg:space-y-5">
        <div>
          <Label
            id="brand"
            className="w-fit text-black text-xs sm:text-[0.825rem] md:text-sm font-medium leading-4 md:leading-5 mb-1 md:mb-[0.375rem]"
          >
            Select a brand
          </Label>
          <Controller
            name="brand"
            control={control}
            render={({ field }) => (
              <Select
                onOpenChange={setBrandSelect}
                value={field.value}
                onValueChange={(val) => {
                  field.onChange(val);
                  setValue("model", "");
                }}
              >
                <SelectTrigger className="bg-white hover:bg-[#FAFAFA] w-full h-10 md:h-[2.875rem] py-[0.625rem] pl-[0.625rem] pr-[0.75rem] sm:py-[0.6875rem] sm:pl-[0.75rem] sm:pr-[0.875rem] md:py-[0.78125rem] md:pl-[0.84375rem] md:pr-[0.9375rem] border border-[#D1D5DB] rounded-xl text-black text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5">
                  <SelectValue
                    placeholder={
                      <span className="text-black/50">Select Brand</span>
                    }
                  />
                  <img
                    src="/assets/chevron-down.svg"
                    className={`transition-transform duration-300 ease-in-out ${
                      brandSelect ? "rotate-180" : ""
                    }`}
                    alt="Chevron"
                  />
                </SelectTrigger>
                <SelectContent className="max-h-60 w-[var(--radix-select-trigger-width)]">
                  <SelectGroup>
                    {uniqueBrands.map((option) => (
                      <SelectItem
                        key={option._id}
                        value={option.brand}
                        className="text-[0.825rem] sm:text-sm font-normal leading-4 md:leading-5 text-black"
                      >
                        {option.brand}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <div>
          <Label
            id="model"
            className="w-fit text-black text-xs sm:text-[0.825rem] md:text-sm font-medium leading-4 md:leading-5 mb-1 md:mb-[0.375rem]"
          >
            Select a model
          </Label>
          <Controller
            name="model"
            control={control}
            render={({ field }) => (
              <Select
                onOpenChange={setModelSelect}
                value={field.value}
                onValueChange={field.onChange}
                disabled={!brand}
              >
                <SelectTrigger className="bg-white hover:bg-[#FAFAFA] w-full min-h-10 md:min-h-[2.875rem] py-[0.625rem] pl-[0.625rem] pr-[0.75rem] sm:py-[0.6875rem] sm:pl-[0.75rem] sm:pr-[0.875rem] md:py-[0.78125rem] md:pl-[0.84375rem] md:pr-[0.9375rem] border border-[#D1D5DB] rounded-xl text-left text-black text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5 [&>span]:line-clamp-none whitespace-normal">
                  <SelectValue
                    placeholder={
                      <span className="text-black/50">
                        {brand ? "Select Model" : "Select Brand first"}
                      </span>
                    }
                  />
                  <img
                    src="/assets/chevron-down.svg"
                    className={`transition-transform duration-300 ease-in-out ${
                      modelSelect ? "rotate-180" : ""
                    }`}
                    alt="Chevron"
                  />
                </SelectTrigger>
                <SelectContent className="max-h-60 w-[var(--radix-select-trigger-width)]">
                  <SelectGroup>
                    {filteredModels.map((option) => (
                      <SelectItem
                        key={option._id}
                        value={option.model}
                        className="text-[0.825rem] sm:text-sm font-normal leading-4 md:leading-5 text-black"
                      >
                        {option.model}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        </div>
      </div>
      <div className="mt-4 sm:mt-5 lg:mt-6">
        <h4 className="mb-3 sm:mb-3.5 lg:mb-4 text-xl sm:text-[1.35rem] lg:text-2xl font-bold leading-5 sm:leading-6 lg:leading-7 text-black font-mobile">
          Pick your sim type
        </h4>
        <Controller
          name="simType"
          control={control}
          render={({ field }) => {
            const bothSelected = !!brand && !!model;
            const capsReady = bothSelected && !!capabilities;
            const radioValue = effectiveRadioSimValue(
              field.value || "",
              eSimAllowed,
              physicalSimAllowed
            );

            return (
              <RadioGroup
                className="gap-3 sm:gap-4 lg:gap-5"
                value={radioValue}
                onValueChange={(v) => {
                  if (
                    (v === "eSim" && eSimAllowed) ||
                    (v === "sim" && physicalSimAllowed)
                  ) {
                    field.onChange(v);
                  }
                }}
                disabled={!capsReady || checkLoading}
              >
                {/* eSIM Option */}
                <div
                  className={cn(
                    "flex items-center justify-between p-2 sm:p-[0.625rem] border rounded-lg sm:rounded-xl transition-colors duration-200",
                    eSimAllowed
                      ? "bg-white border-[#D1D5DB] hover:border-[#C6CBD2]"
                      : "bg-[#F0F0F0] border-[#F0F0F0] opacity-50 cursor-not-allowed",
                    radioValue === "eSim" &&
                      eSimAllowed &&
                      "border-[#FE4D01] bg-[#FE4D010D]"
                  )}
                >
                  <div className="flex gap-1.5 sm:gap-2">
                    <RadioGroupItem
                      className="mt-[0.1rem]"
                      value="eSim"
                      id="eSim"
                      disabled={!eSimAllowed || !capsReady}
                    />
                    <div>
                      <Label
                        htmlFor="eSim"
                        className={cn(
                          "w-fit mb-0 text-black text-[0.72rem] sm:text-xs font-bold leading-4 sm:leading-5",
                          "transition-opacity duration-200 ease-in-out",
                          eSimAllowed ? "cursor-pointer" : "cursor-not-allowed"
                        )}
                      >
                        eSIM (digital delivery)
                      </Label>
                      <span className="inline-block text-[0.72rem] sm:text-xs font-medium leading-4 sm:leading-5 text-black opacity-50">
                        Only works on newer devices
                      </span>
                    </div>
                  </div>
                  <div className="flex-shrink-0 size-10 sm:size-[2.625rem]">
                    <img
                      src="/assets/e-sim.webp"
                      className="w-full h-auto"
                      alt="eSIM"
                    />
                  </div>
                </div>
                {/* Physical SIM Option */}
                <div
                  className={cn(
                    "flex items-center justify-between p-2 sm:p-[0.625rem] border rounded-lg sm:rounded-xl transition-colors duration-200",
                    physicalSimAllowed
                      ? "bg-white border-[#D1D5DB] hover:border-[#C6CBD2]"
                      : "bg-[#F0F0F0] border-[#F0F0F0] opacity-50 cursor-not-allowed",
                    radioValue === "sim" &&
                      physicalSimAllowed &&
                      "border-[#FE4D01] bg-[#FE4D010D]"
                  )}
                >
                  <div className="flex gap-1.5 sm:gap-2">
                    <RadioGroupItem
                      className="mt-[0.1rem]"
                      value="sim"
                      id="sim"
                      disabled={!physicalSimAllowed || !capsReady}
                    />
                    <div>
                      <Label
                        htmlFor="sim"
                        className={cn(
                          "w-fit mb-0 text-black text-[0.72rem] sm:text-xs font-bold leading-4 sm:leading-5",
                          "transition-opacity duration-200 ease-in-out",
                          physicalSimAllowed
                            ? "cursor-pointer"
                            : "cursor-not-allowed"
                        )}
                      >
                        Physical SIM card
                      </Label>
                      <span className="inline-block text-[0.72rem] sm:text-xs font-medium leading-4 sm:leading-5 text-black opacity-50">
                        works on all devices
                      </span>
                    </div>
                  </div>
                  <div className="flex-shrink-0 size-10 sm:size-[2.625rem]">
                    <img
                      src="/assets/sim.webp"
                      className="w-full h-auto"
                      alt="SIM"
                    />
                  </div>
                </div>
              </RadioGroup>
            );
          }}
        />
      </div>
      {brand && model && checkDataForBanner?.data && (
        <div className="flex gap-1.5 sm:gap-2 bg-[#F2FFEC] p-2.5 sm:p-3 lg:p-[0.875rem] rounded-lg sm:rounded-xl mt-3.5 sm:mt-4 lg:mt-5">
          <div className="flex-shrink-0 size-4 sm:size-5 mt-0.5 sm:mt-1">
            <img
              src="/assets/green-tick.svg"
              className="w-full h-auto"
              alt="Tick"
            />
          </div>
          <p className="text-[#3C3C3C] text-[0.725rem] sm:text-xs font-medium leading-4 sm:leading-5">
            Congrats! Your {model} works with{" "}
            {checkDataForBanner?.data?.[0]?.deviceSupport?.simCapabilities
              ?.eSIMOnly
              ? "ESIM"
              : checkDataForBanner?.data?.[0]?.deviceSupport?.simCapabilities
                  ?.singleSIMWithESIM
              ? "both ESIM and physical SIM"
              : checkDataForBanner?.data?.[0]?.deviceSupport?.simCapabilities
                  ?.dualSIM
              ? "two physical SIM"
              : "physical SIM"}{" "}
            cards. After checkout, we’ll email your new SIM. If you already have
            a VLE Mobile SIM, you can activate it in the next step.
          </p>
        </div>
      )}
    </div>
  );
};
